import type {
  CaptureItem,
  ConsistencyEvidence,
  EntityId,
  Habit,
  HabitCheckin,
  Reminder,
  Review,
  ReviewOutcome,
  Routine,
  RoutineStep,
  HabitStatus,
  Problem,
  ProblemPatch,
  ProblemStatus,
  ControlCategory,
  LimiterType,
  Project,
  ProjectPatch,
  ProjectStatus,
  CalendarEvent,
  EventPatch,
  EventStatus,
  Recurrence,
  Account,
  AccountType,
  Transaction,
  TransactionKind,
  ImportBatch,
  Note,
  NotePatch,
  Goal,
  GoalPatch,
  GoalStatus,
  Pillar,
  GoalHorizon,
  Milestone,
  MilestoneStatus,
  DirectionItem,
  DirectionLens,
  HealthLog,
  StudySubject,
  StudySession,
  MasteryState,
  SubjectStatus,
  Opportunity,
  OpportunityPatch,
  OpportunityStage,
  JournalEntry,
  JournalKind,
  JournalStream,
  ContentItem,
  ContentItemPatch,
  ContentStage,
  Venture,
  VenturePatch,
  VentureStatus,
  InventoryItem,
  InventoryItemPatch,
  ItemCategory,
  ItemStatus,
  FocusSession,
  SecurityConfig,
  SecurityEvent,
  SecurityEventType,
  Profile,
  EducationRecord,
  Admission,
  OfferStatus,
  AdmissionStatus,
  FundingSource,
  FundingKind,
  FundingStatus,
  TouchpointChannel,
  EducationProgram,
  EducationProgramInput,
  ResumeProfile,
  ResumeProfileInput,
  SourceRecord,
  SourceRecordId,
  SourceReviewState,
  Target,
  TargetStatus,
  TargetType,
  TargetCadence,
  Task,
  TaskLane,
  RoutineDayType,
  WeekdayMask,
} from '@life-os/app-core';

export interface SourceRepository {
  preserve(input: {
    ownerId: string;
    privacyClass: SourceRecord['privacyClass'];
    origin: SourceRecord['origin'];
    rawBody: string;
    originalPath?: string | null;
  }): Promise<SourceRecord>;
  get(id: SourceRecordId): Promise<SourceRecord | undefined>;
  appendPromotion(id: SourceRecordId, entityId: EntityId): Promise<SourceRecord>;
  setReviewState(id: SourceRecordId, state: SourceReviewState): Promise<SourceRecord>;
  list(): Promise<readonly SourceRecord[]>;
}

export interface CaptureRepository {
  create(args: {
    ownerId: string;
    rawText: string;
    captureType: CaptureItem['captureType'];
    privacyClass: CaptureItem['privacyClass'];
    sourceRecordId: SourceRecordId;
  }): Promise<CaptureItem>;
  get(id: EntityId): Promise<CaptureItem | undefined>;
  listUnreviewed(): Promise<readonly CaptureItem[]>;
  markReviewed(id: EntityId, outcome: ReviewOutcome): Promise<CaptureItem>;
}

export interface TaskRepository {
  create(args: {
    ownerId: string;
    title: string;
    lane: TaskLane;
    scheduledFor: string | null;
    privacyClass: Task['privacyClass'];
    links: Task['links'];
  }): Promise<Task>;
  get(id: EntityId): Promise<Task | undefined>;
  listByLane(lane: TaskLane): Promise<readonly Task[]>;
  listScheduledOn(dateIso: string): Promise<readonly Task[]>;
  move(id: EntityId, lane: TaskLane, scheduledFor: string | null): Promise<Task>;
  complete(id: EntityId): Promise<Task>;
}

export interface ReviewRepository {
  create(args: {
    ownerId: string;
    reviewType: Review['reviewType'];
    forDate: string;
    notes: string;
    touchedIds: readonly EntityId[];
  }): Promise<Review>;
  listForDate(dateIso: string): Promise<readonly Review[]>;
  listRecent(limit: number): Promise<readonly Review[]>;
}

export interface ReminderRepository {
  create(args: { ownerId: string; label: string; linkedId: EntityId | null; fireAt: string; privacyClass: Reminder['privacyClass'] }): Promise<Reminder>;
  listFiringOn(dateIso: string): Promise<readonly Reminder[]>;
  listAll(): Promise<readonly Reminder[]>;
  setState(id: EntityId, state: Reminder['state']): Promise<Reminder>;
}

export interface TargetRepository {
  createTarget(args: {
    ownerId: string;
    title: string;
    targetType: TargetType;
    cadence: TargetCadence;
    quantity: number;
    qualityBar: string;
    evidenceRule: string;
    minimumViableAction: string;
    privacyClass: Target['privacyClass'];
  }): Promise<Target>;
  getTarget(id: EntityId): Promise<Target | undefined>;
  listTargets(): Promise<readonly Target[]>;
  setStatus(id: EntityId, status: TargetStatus): Promise<Target>;
  addEvidence(args: { ownerId: string; targetId: EntityId; occurredOn: string; note: string; privacyClass: Target['privacyClass'] }): Promise<ConsistencyEvidence>;
  listEvidence(targetId: EntityId): Promise<readonly ConsistencyEvidence[]>;
}

export interface RoutineRepository {
  createHabit(args: {
    ownerId: string;
    title: string;
    weekdayMask: WeekdayMask;
    privacyClass: Habit['privacyClass'];
  }): Promise<Habit>;
  getHabit(id: EntityId): Promise<Habit | undefined>;
  listHabits(): Promise<readonly Habit[]>;
  setHabitStatus(id: EntityId, status: HabitStatus): Promise<Habit>;
  addCheckin(args: { ownerId: string; habitId: EntityId; occurredOn: string; note: string; privacyClass: Habit['privacyClass'] }): Promise<HabitCheckin>;
  listCheckins(habitId: EntityId): Promise<readonly HabitCheckin[]>;
  createRoutine(args: {
    ownerId: string;
    title: string;
    dayType: RoutineDayType;
    weekdayMask: WeekdayMask;
    steps: readonly RoutineStep[];
    privacyClass: Routine['privacyClass'];
  }): Promise<Routine>;
  getRoutine(id: EntityId): Promise<Routine | undefined>;
  listRoutines(): Promise<readonly Routine[]>;
  markRan(id: EntityId, ranAtIso: string): Promise<Routine>;
  setRoutineStatus(id: EntityId, status: HabitStatus): Promise<Routine>;
}

export interface ProblemRepository {
  createProblem(args: {
    ownerId: string;
    title: string;
    controlCategory: ControlCategory;
    limiterType: LimiterType;
    cause: string;
    intervention: string;
    nextStep: string;
    privacyClass: Problem['privacyClass'];
  }): Promise<Problem>;
  getProblem(id: EntityId): Promise<Problem | undefined>;
  listProblems(): Promise<readonly Problem[]>;
  updateProblem(id: EntityId, patch: ProblemPatch): Promise<Problem>;
  setStatus(id: EntityId, status: ProblemStatus): Promise<Problem>;
}

export interface ProjectRepository {
  createProject(args: {
    ownerId: string;
    title: string;
    summary: string;
    nextAction: string;
    definitionOfDone: string;
    privacyClass: Project['privacyClass'];
  }): Promise<Project>;
  getProject(id: EntityId): Promise<Project | undefined>;
  listProjects(): Promise<readonly Project[]>;
  updateProject(id: EntityId, patch: ProjectPatch): Promise<Project>;
  setStatus(id: EntityId, status: ProjectStatus): Promise<Project>;
}

export interface EventRepository {
  createEvent(args: {
    ownerId: string;
    title: string;
    startsAt: string;
    durationMinutes: number;
    location: string;
    recurrence: Recurrence;
    privacyClass: CalendarEvent['privacyClass'];
  }): Promise<CalendarEvent>;
  getEvent(id: EntityId): Promise<CalendarEvent | undefined>;
  listEvents(): Promise<readonly CalendarEvent[]>;
  updateEvent(id: EntityId, patch: EventPatch): Promise<CalendarEvent>;
  setStatus(id: EntityId, status: EventStatus): Promise<CalendarEvent>;
}

export interface FinanceRepository {
  createAccount(args: {
    ownerId: string;
    name: string;
    accountType: AccountType;
    currency: string;
    openingBalanceMinor: number;
    privacyClass: Account['privacyClass'];
  }): Promise<Account>;
  getAccount(id: EntityId): Promise<Account | undefined>;
  listAccounts(): Promise<readonly Account[]>;
  createTransaction(args: {
    ownerId: string;
    accountId: EntityId;
    txnKind: TransactionKind;
    amountMinor: number;
    category: string;
    occurredOn: string;
    note: string;
    occurredAt: string | null;
    counterparty: string | null;
    externalReference: string | null;
    rail: Transaction['rail'];
    balanceAfterMinor: number | null;
    importFingerprint: string | null;
    importBatchId: EntityId | null;
    privacyClass: Transaction['privacyClass'];
  }): Promise<Transaction>;
  listTransactions(accountId?: EntityId): Promise<readonly Transaction[]>;
  createImportBatch(args: {
    ownerId: string;
    accountId: EntityId;
    sourceLabel: string;
    sourceFiles: readonly string[];
    rowsSeen: number;
    rowsCreated: number;
    rowsSkipped: number;
    importedOn: string;
    note: string;
    privacyClass: ImportBatch['privacyClass'];
  }): Promise<ImportBatch>;
  listImportBatches(): Promise<readonly ImportBatch[]>;
}

export interface NoteRepository {
  createNote(args: {
    ownerId: string;
    title: string;
    blocks: Note['blocks'];
    tags: readonly string[];
    privacyClass: Note['privacyClass'];
  }): Promise<Note>;
  getNote(id: EntityId): Promise<Note | undefined>;
  listNotes(): Promise<readonly Note[]>;
  updateNote(id: EntityId, patch: NotePatch): Promise<Note>;
  archiveNote(id: EntityId): Promise<Note>;
}

export interface DirectionRepository {
  createGoal(args: {
    ownerId: string;
    title: string;
    pillar: Pillar;
    horizon: GoalHorizon;
    why: string;
    privacyClass: Goal['privacyClass'];
  }): Promise<Goal>;
  getGoal(id: EntityId): Promise<Goal | undefined>;
  listGoals(): Promise<readonly Goal[]>;
  updateGoal(id: EntityId, patch: GoalPatch): Promise<Goal>;
  setGoalStatus(id: EntityId, status: GoalStatus): Promise<Goal>;
  createMilestone(args: {
    ownerId: string;
    title: string;
    contributesToGoalIds: readonly EntityId[];
    privacyClass: Milestone['privacyClass'];
  }): Promise<Milestone>;
  getMilestone(id: EntityId): Promise<Milestone | undefined>;
  listMilestones(): Promise<readonly Milestone[]>;
  setMilestoneStatus(id: EntityId, status: MilestoneStatus): Promise<Milestone>;
  setMilestoneGoals(id: EntityId, goalIds: readonly EntityId[]): Promise<Milestone>;
  createItem(args: {
    ownerId: string;
    lens: DirectionLens;
    itemKind: DirectionItem['itemKind'];
    title: string;
    detail: string;
    posture: ControlCategory | null;
    privacyClass: DirectionItem['privacyClass'];
  }): Promise<DirectionItem>;
  getItem(id: EntityId): Promise<DirectionItem | undefined>;
  listItems(lens: DirectionLens): Promise<readonly DirectionItem[]>;
  setItemPosture(id: EntityId, posture: ControlCategory | null): Promise<DirectionItem>;
  archiveItem(id: EntityId): Promise<DirectionItem>;
}

export interface HealthRepository {
  createLog(args: {
    ownerId: string;
    occurredOn: string;
    sleepHours: number;
    energy: number;
    movementMinutes: number;
    note: string;
    privacyClass: HealthLog['privacyClass'];
  }): Promise<HealthLog>;
  listLogs(): Promise<readonly HealthLog[]>;
}

export interface StudyRepository {
  createSubject(args: {
    ownerId: string;
    title: string;
    masteryState: MasteryState;
    privacyClass: StudySubject['privacyClass'];
  }): Promise<StudySubject>;
  getSubject(id: EntityId): Promise<StudySubject | undefined>;
  listSubjects(): Promise<readonly StudySubject[]>;
  setMastery(id: EntityId, state: MasteryState): Promise<StudySubject>;
  setSubjectStatus(id: EntityId, status: SubjectStatus): Promise<StudySubject>;
  createSession(args: {
    ownerId: string;
    subjectId: EntityId;
    minutes: number;
    occurredOn: string;
    note: string;
    privacyClass: StudySession['privacyClass'];
  }): Promise<StudySession>;
  listSessions(subjectId?: EntityId): Promise<readonly StudySession[]>;
}

export interface CareerRepository {
  createOpportunity(args: {
    ownerId: string;
    title: string;
    organization: string;
    url: string;
    note: string;
    privacyClass: Opportunity['privacyClass'];
  }): Promise<Opportunity>;
  getOpportunity(id: EntityId): Promise<Opportunity | undefined>;
  listOpportunities(): Promise<readonly Opportunity[]>;
  updateOpportunity(id: EntityId, patch: OpportunityPatch): Promise<Opportunity>;
  setStage(id: EntityId, stage: OpportunityStage): Promise<Opportunity>;
}

export interface JournalRepository {
  createEntry(args: {
    ownerId: string;
    stream: JournalStream;
    entryKind: JournalKind;
    title: string;
    body: string;
    occurredOn: string;
    privacyClass: JournalEntry['privacyClass'];
  }): Promise<JournalEntry>;
  listEntries(stream?: JournalStream): Promise<readonly JournalEntry[]>;
}

export interface ContentRepository {
  createItem(args: {
    ownerId: string;
    title: string;
    platform: string;
    note: string;
    url: string;
    privacyClass: ContentItem['privacyClass'];
  }): Promise<ContentItem>;
  getItem(id: EntityId): Promise<ContentItem | undefined>;
  listItems(): Promise<readonly ContentItem[]>;
  updateItem(id: EntityId, patch: ContentItemPatch): Promise<ContentItem>;
  setStage(id: EntityId, stage: ContentStage): Promise<ContentItem>;
}

export interface BusinessRepository {
  createVenture(args: {
    ownerId: string;
    title: string;
    sector: string;
    monthlyRevenueMinor: number;
    note: string;
    privacyClass: Venture['privacyClass'];
  }): Promise<Venture>;
  getVenture(id: EntityId): Promise<Venture | undefined>;
  listVentures(): Promise<readonly Venture[]>;
  updateVenture(id: EntityId, patch: VenturePatch): Promise<Venture>;
  setStatus(id: EntityId, status: VentureStatus): Promise<Venture>;
}

export interface EnvironmentRepository {
  createItem(args: {
    ownerId: string;
    name: string;
    category: ItemCategory;
    location: string;
    note: string;
    privacyClass: InventoryItem['privacyClass'];
  }): Promise<InventoryItem>;
  getItem(id: EntityId): Promise<InventoryItem | undefined>;
  listItems(): Promise<readonly InventoryItem[]>;
  updateItem(id: EntityId, patch: InventoryItemPatch): Promise<InventoryItem>;
  setStatus(id: EntityId, status: ItemStatus): Promise<InventoryItem>;
}

export interface FocusRepository {
  createSession(args: {
    ownerId: string;
    minutes: number;
    occurredOn: string;
    note: string;
    linkedId: EntityId | null;
    privacyClass: FocusSession['privacyClass'];
  }): Promise<FocusSession>;
  listSessions(): Promise<readonly FocusSession[]>;
}

export interface AcademicsRepository {
  upsertProfile(args: {
    ownerId: string;
    displayName: string;
    headline: string;
    location: string;
    education: readonly EducationRecord[];
    links: readonly { readonly label: string; readonly url: string }[];
    privacyClass: Profile['privacyClass'];
  }): Promise<Profile>;
  getProfile(): Promise<Profile | undefined>;
  createAdmission(args: {
    ownerId: string;
    institution: string;
    programme: string;
    offerStatus: OfferStatus;
    conditions: readonly string[];
    currency: string;
    tuitionMinor: number;
    depositMinor: number;
    livingEstimateMinor: number;
    depositDueDate: string | null;
    decisionDate: string | null;
    intakeTerm: string;
    note: string;
    privacyClass: Admission['privacyClass'];
  }): Promise<Admission>;
  listAdmissions(): Promise<readonly Admission[]>;
  setOfferStatus(id: EntityId, offerStatus: OfferStatus): Promise<Admission>;
  setAdmissionStatus(id: EntityId, status: AdmissionStatus): Promise<Admission>;
  createFunding(args: {
    ownerId: string;
    name: string;
    sourceKind: FundingKind;
    status: FundingStatus;
    currency: string;
    amountMinor: number;
    fullyFunded: boolean;
    opensOn: string | null;
    closesOn: string | null;
    note: string;
    nextAction: string;
    nextActionDate: string | null;
    privacyClass: FundingSource['privacyClass'];
  }): Promise<FundingSource>;
  listFunding(): Promise<readonly FundingSource[]>;
  setFundingStatus(id: EntityId, status: FundingStatus): Promise<FundingSource>;
  setFundingNextAction(id: EntityId, nextAction: string, nextActionDate: string | null): Promise<FundingSource>;
  logTouchpoint(id: EntityId, touchpoint: { on: string; channel: TouchpointChannel; note: string }): Promise<FundingSource>;
  upsertEducationProgram(args: {
    ownerId: string;
    program: EducationProgramInput;
    privacyClass: EducationProgram['privacyClass'];
  }): Promise<EducationProgram>;
  listEducationPrograms(): Promise<readonly EducationProgram[]>;
  upsertResumeProfile(args: {
    ownerId: string;
    profile: ResumeProfileInput;
    privacyClass: ResumeProfile['privacyClass'];
  }): Promise<ResumeProfile>;
  getResumeProfile(): Promise<ResumeProfile | undefined>;
}

export interface SecurityRepository {
  getConfig(): Promise<SecurityConfig | undefined>;
  putConfig(config: SecurityConfig): Promise<void>;
  appendEvent(args: { ownerId: string; eventType: SecurityEventType; at: string; detail: string }): Promise<SecurityEvent>;
  listEvents(limit: number): Promise<readonly SecurityEvent[]>;
}

export class RecordNotFoundError extends Error {
  constructor(kind: string, id: string) {
    super(`${kind} not found: ${id}`);
    this.name = 'RecordNotFoundError';
  }
}

export class InvalidTransitionError extends Error {
  constructor(detail: string) {
    super(`Invalid transition: ${detail}`);
    this.name = 'InvalidTransitionError';
  }
}
